import { Link } from "react-router-dom";
import { Section, Card, CTA } from "@/components/Section";
import { ArrowRight, CheckCircle2, Search, PenLine, Wrench, Target } from "lucide-react";

const values = [
  "Leadership before technology",
  "Diagnose before design",
  "Strategize before build",
  "Disciplined execution",
  "Responsible AI",
  "Human judgment always",
  "Measured outcomes",
  "Practical, immediate value",
];

const OPERATING_METHOD = [
  {
    icon: Search,
    n: "Diagnose",
    b: "Root-cause analysis of the challenge, the responsibilities, and the enterprise outcome the leader is accountable for.",
  },
  {
    icon: PenLine,
    n: "Strategize",
    b: "Design a leadership-first strategy, with AI, systems, and process choices in service of the outcome.",
  },
  {
    icon: Wrench,
    n: "Build",
    b: "Advise, equip, or build alongside the team. Never a hand-off, never a stack of unused deliverables.",
  },
  {
    icon: Target,
    n: "Execute & Evaluate",
    b: "Execute with discipline; measure success strictly by realization of the intended enterprise outcome.",
  },
];

export default function About() {
  return (
    <div data-testid="page-about">
      {/* Founder + Authority */}
      <Section
        eyebrow="About Patricia"
        title="Experience You Can Trust. Innovation That Works for You."
        kicker="LAYOR Digital Group is founded and led by Patricia Royal, Ed. S. Every engagement begins with listening, understanding your priorities, and respecting the work you are already responsible for."
      >
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="md:col-span-5">
            <div className="relative rounded-3xl border border-[var(--layor-border)] p-3 bg-white shadow-[0_30px_80px_-30px_rgba(10,21,44,0.28)] sticky top-24">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[var(--layor-gray)]">
                <img
                  src="/brand/patricia-royal.jpg"
                  alt="Patricia Royal, Ed. S., Founder of LAYOR Digital Group"
                  className="w-full h-full object-cover"
                  data-testid="about-founder-photo"
                />
              </div>
              <div className="absolute -top-3 -right-3 rounded-full bg-[var(--layor-navy)] text-[var(--layor-gold)] text-[10px] uppercase tracking-[0.3em] font-semibold px-3 py-1.5 border border-[var(--layor-gold)]/50">
                Founder &amp; CEO
              </div>
              <div className="mt-4 px-2 pb-1">
                <div className="font-display text-lg text-[var(--layor-navy)]">
                  Patricia Royal, Ed. S.
                </div>
                <div className="text-xs text-[var(--layor-muted)] mt-1">
                  Executive Advisor · Leadership Coach · Strategic Consultant · AI Strategist
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7 space-y-6">
            <div className="rounded-2xl bg-white border-l-4 border-[var(--layor-gold)] p-6 shadow-sm">
              <div className="eyebrow text-[var(--layor-pink)]">Founder</div>
              <p className="mt-3 text-[var(--layor-navy)] text-[17px] leading-relaxed">
                Patricia brings 30-plus years of progressive leadership experience to help organizations clarify direction, solve complex challenges, and move forward with confidence. Her guidance is grounded in real leadership, the kind that has led people, systems, operations, budgets, boards, and strategy at the executive level.
              </p>
            </div>

            <div className="rounded-2xl p-6" style={{ backgroundColor: "var(--layor-navy)" }}>
              <div className="eyebrow font-semibold" style={{ color: "#E6C871" }}>
                How LAYOR Works With You
              </div>
              <p className="mt-3 text-white text-[16px] leading-relaxed">
                When AI and advanced digital tools can strengthen the work, LAYOR integrates them thoughtfully to expand leadership capacity, improve decision-making, and support more effective execution. Human judgment, experience, and meaningful connection remain at the center of every solution.
              </p>
            </div>

            <Card>
              <div className="eyebrow text-[var(--layor-pink)]">Operational Philosophy</div>
              <blockquote className="mt-4 text-[var(--layor-navy)] leading-relaxed italic text-[17px]">
                "Diagnose before we design. Strategize before we build. Execute with absolute discipline. And measure success strictly by realization of the intended enterprise outcome."
              </blockquote>
              <div className="mt-4 text-sm font-semibold text-[var(--layor-navy)]">
                Patricia Royal, Ed. S.
              </div>
            </Card>
          </div>
        </div>
      </Section>

      {/* Vision & Mission */}
      <Section
        eyebrow="Vision & Mission"
        title="Executive judgment at the center. Practical AI in service of the outcome."
        className="bg-white border-y border-[var(--layor-border)]"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <div className="eyebrow" style={{ color: "#C6A85A" }}>Vision</div>
            <p className="mt-3 text-[var(--layor-navy)] leading-relaxed text-[16px]">
              Premier global catalyst for enterprise transformation where elite human leadership and advanced technology converge to drive unprecedented organizational velocity.
            </p>
          </Card>
          <Card>
            <div className="eyebrow" style={{ color: "#E33278" }}>Mission</div>
            <p className="mt-3 text-[var(--layor-navy)] leading-relaxed text-[16px]">
              Diagnose friction through rigorous advisory, equip leaders with real-world execution capabilities, and engineer practical digital solutions for immediate, measurable impact, with human judgment remaining the foundation.
            </p>
          </Card>
        </div>
      </Section>

      {/* About LAYOR narrative */}
      <Section eyebrow="About LAYOR" title="A premium, listening-first executive advisory and transformation firm.">
        <div className="max-w-3xl space-y-5 text-[var(--layor-navy)]/85 text-[16px] leading-relaxed">
          <p>
            LAYOR Digital Group is a premium executive advisory and transformation firm that also equips leaders and builds practical solutions. Every engagement begins with listening to your priorities, understanding your organization, and respecting the work you are already responsible for.
          </p>
          <p>
            Clients come to LAYOR when demanding work needs smarter systems, when strategy has become unclear, when execution has been inconsistent, or when AI is entering the organization and the leadership team wants a responsible, human-centered path forward. Every recommendation is grounded in real leadership experience.
          </p>
        </div>
      </Section>

      {/* Strategic Discovery & Engagement */}
      <Section
        eyebrow="Strategic Discovery & Engagement"
        title="How every LAYOR engagement begins."
        className="bg-white border-y border-[var(--layor-border)]"
      >
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div>
            <p className="text-[var(--layor-navy)]/85 leading-relaxed text-[16px]">
              Before any strategy, training program, or built solution, LAYOR walks with the
              executive through a structured discovery. We surface the responsibilities you're
              carrying, the outcomes you're accountable for, the systems you rely on, and the
              friction that is slowing decisions or execution.
            </p>
            <p className="mt-4 text-[var(--layor-navy)]/85 leading-relaxed text-[16px]">
              Only then do we recommend whether the right path is advisory, an executive-team
              engagement, custom training, an AI/digital build, or a combination. Nothing is
              prescribed until the diagnosis is understood.
            </p>
          </div>
          <div className="rounded-2xl bg-[var(--layor-ivory)] border border-[var(--layor-border)] p-6">
            <div className="eyebrow" style={{ color: "#E33278" }}>What Discovery Produces</div>
            <ul className="mt-4 space-y-3">
              {[
                "A clear articulation of the enterprise outcome to be realized",
                "Root-cause analysis of what is currently in the way",
                "A recommended engagement path (Advise / Equip / Build / mixed)",
                "Scope, timeline, and executive-time expectations",
                "Explicit success measures tied to the outcome",
              ].map((it) => (
                <li key={it} className="flex items-start gap-3 text-[var(--layor-navy)]">
                  <CheckCircle2 size={18} className="text-[var(--layor-gold)] mt-0.5 shrink-0" />
                  <span className="text-sm leading-snug">{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Strategic Technology Integration & Applied Learning */}
      <Section
        eyebrow="Strategic Technology Integration & Applied Learning"
        title="Thoughtful integration. Human judgment at the center."
      >
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div>
            <p className="text-[var(--layor-navy)]/85 leading-relaxed text-[16px]">
              LAYOR integrates practical AI, workflow design, and digital solutions when they meaningfully expand capacity, sharpen decisions, or ease execution. Every recommendation is shaped by executive judgment and the enterprise outcome you are working toward.
            </p>
            <p className="mt-4 text-[var(--layor-navy)]/85 leading-relaxed text-[16px]">
              Applied learning is at the heart of every training LAYOR delivers. Participants work on authentic leadership challenges from their own work, use their preferred AI platform (such as ChatGPT), and leave with practical tools they built and can use immediately.
            </p>
          </div>
          <div className="rounded-2xl bg-[var(--layor-navy)] p-6 md:p-7 text-white">
            <div className="eyebrow font-semibold" style={{ color: "#E6C871" }}>
              How We Show Up
            </div>
            <ul className="mt-4 space-y-3 text-white/90">
              {[
                "Human judgment, experience, and connection stay at the center",
                "Privacy, confidentiality, and verification are designed in",
                "One focused platform or process at a time, clarity over overload",
                "Hands-on, interactive, and authentic to the work you already lead",
                "Participants leave with something they built and can use",
              ].map((it) => (
                <li key={it} className="flex items-start gap-3 text-sm leading-snug">
                  <CheckCircle2 size={16} style={{ color: "#E6C871" }} className="mt-0.5 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Operating Method */}
      <Section
        eyebrow="How LAYOR Operates"
        title="Diagnose. Strategize. Build. Execute & Evaluate."
        className="bg-white border-y border-[var(--layor-border)]"
      >
        <div className="grid md:grid-cols-4 gap-4 md:gap-5">
          {OPERATING_METHOD.map(({ icon: Icon, n, b }, i) => (
            <div
              key={n}
              className="rounded-2xl border border-[var(--layor-border)] bg-[var(--layor-ivory)] p-6"
              data-testid={`operating-${i}`}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--layor-navy)] text-[var(--layor-gold)]">
                <Icon size={20} />
              </div>
              <div
                className="mt-5 text-[10px] uppercase tracking-[0.35em] font-semibold"
                style={{ color: i === 3 ? "#E33278" : "#C6A85A" }}
              >
                Step {i + 1}
              </div>
              <h3 className="mt-2 font-display text-lg text-[var(--layor-navy)] leading-snug">
                {n}
              </h3>
              <p className="mt-3 text-sm text-[var(--layor-muted)] leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Values */}
      <Section eyebrow="Values" title="Commitments that shape every engagement.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {values.map((v, i) => (
            <div
              key={v}
              className="flex items-start gap-3 bg-white border border-[var(--layor-border)] rounded-xl p-4"
              data-testid={`value-${i}`}
            >
              <CheckCircle2 size={18} className="text-[var(--layor-gold)] mt-0.5 shrink-0" />
              <span className="text-sm font-semibold text-[var(--layor-navy)]">{v}</span>
            </div>
          ))}
        </div>
      </Section>

      <CTA
        dark
        headline="Bring your enterprise outcome. We'll walk you through the path to realize it."
        sub="Every engagement starts with a consultation to diagnose the work and recommend the honest next step: advisory, an executive-team engagement, custom training, a built solution, or a combination."
        actions={
          <>
            <Link to="/contact" className="btn-gold rounded-full px-5 py-3 text-sm font-semibold">
              Book a Consultation
            </Link>
            <Link
              to="/services"
              className="rounded-full px-5 py-3 text-sm font-semibold border border-white/30 text-white hover:bg-white hover:text-[var(--layor-navy)] inline-flex items-center gap-2"
            >
              Explore How We Help <ArrowRight size={14} />
            </Link>
          </>
        }
      />
    </div>
  );
}
